import {useContext} from "react";
import {ChangesContext, ToastContext} from "../Contexts";
import {
    CatalogLanguage,
    ToastFailureMessage,
    ToastSuccessMessage,
    WebsiteLanguage,
    WebsiteLanguageCreateRequest,
    WebsiteLanguageUpdateRequest
} from "../Models/Models";
import HttpRepository from "./HttpRepository";

// The catalog is static on the server, so one request serves the whole session.
let catalogPromise: Promise<CatalogLanguage[]> | null = null;

export function loadLanguageCatalog(): Promise<CatalogLanguage[]> {
    if (!catalogPromise) {
        catalogPromise = HttpRepository.getLanguageCatalog()
            .then(resp => resp.data)
            .catch(error => {
                catalogPromise = null;
                throw error;
            });
    }
    return catalogPromise;
}

export function WebsiteLanguageService() {
    let {setToast}: any = useContext(ToastContext);
    let {setHasChanges}: any = useContext(ChangesContext);

    async function getCatalog(): Promise<CatalogLanguage[]> {
        try {
            return await loadLanguageCatalog();
        } catch (error) {
            setToast({message: ToastFailureMessage.dataNotLoaded});
            return [];
        }
    }

    async function getSiteLanguages(websiteId: number): Promise<WebsiteLanguage[] | undefined> {
        try {
            return (await HttpRepository.getWebsiteLanguages(websiteId)).data;
        } catch (error) {
            setToast({message: ToastFailureMessage.dataNotLoaded});
        }
    }

    async function addLanguage(request: WebsiteLanguageCreateRequest): Promise<WebsiteLanguage | undefined> {
        try {
            const created = (await HttpRepository.createWebsiteLanguage(request)).data;
            setHasChanges(true);
            setToast({message: ToastSuccessMessage.dataSaved});
            return created;
        } catch (error) {
            setToast({message: ToastFailureMessage.dataNotSaved});
        }
    }

    async function updateLanguage(request: WebsiteLanguageUpdateRequest): Promise<WebsiteLanguage | undefined> {
        try {
            const updated = (await HttpRepository.updateWebsiteLanguage(request)).data;
            setHasChanges(true);
            setToast({message: ToastSuccessMessage.changesSaved});
            return updated;
        } catch (error) {
            setToast({message: ToastFailureMessage.changesNotSaved});
        }
    }

    async function deleteLanguage(id: number): Promise<boolean> {
        try {
            await HttpRepository.deleteWebsiteLanguage(id);
            setHasChanges(true);
            setToast({message: ToastSuccessMessage.changesSaved});
            return true;
        } catch (error) {
            setToast({message: ToastFailureMessage.changesNotSaved});
            return false;
        }
    }

    return {getCatalog, getSiteLanguages, addLanguage, updateLanguage, deleteLanguage}
}
